import { useState, type ReactNode } from "react";
import type { AgreementLinkView } from "../api/types.js";
import { Async, Failure } from "./Async.jsx";
import { CopyField } from "./CopyField.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { longDate } from "../lib/format.js";

/**
 * The public link anyone can sign the company's agreement through.
 *
 * WHY IT LIVES ON THE COMPANY SCREEN. Beside the wording it publishes, because
 * the two are one decision: the link is an address for those words, and a link
 * minted before the words are written shows a customer a signature page under
 * an empty document. Having them on one screen is what makes that obvious.
 *
 * THE OPPOSITE OF EVERY OTHER CREDENTIAL HERE. A share link is handed to one
 * named person and a hand-off credential is a secret between two machines;
 * this one is meant to be published, so the address is shown rather than the
 * raw token, and losing it costs a revoke and a re-mint rather than anything
 * worse.
 *
 * THE FIELD APP READS ONE OF THESE (migration 0080). The app carries no company
 * and is never typed into, so the address reaches a surveyor from here. The
 * first link minted goes to the app by itself; a later one replaces it only
 * when somebody says so, because a link on a website being made should not
 * silently change what is shown on doorsteps.
 */
export function AgreementLinks({ canEdit }: { readonly canEdit: boolean }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<{
    readonly links: AgreementLinkView[];
    readonly appUrl: string | null;
  }>(async () => {
    const [links, appUrl] = await Promise.all([client.agreementLinks(), client.fieldAgreementLink()]);
    return { links, appUrl };
  }, [client]);

  return (
    <>
      <h2>Your public signing link</h2>
      <p className="hint small">
        One address anyone can open to read the agreement above, enter their
        name, email and address, and sign it. Put it in an email, a message or
        on your website. Each signature makes its own signed PDF and arrives as
        a job here.
      </p>
      <Async state={state} what="your signing links" reload={reload}>
        {(data) => <Body links={data.links} appUrl={data.appUrl} canEdit={canEdit} reload={reload} />}
      </Async>
    </>
  );
}

function Body({
  links,
  appUrl,
  canEdit,
  reload,
}: {
  readonly links: readonly AgreementLinkView[];
  readonly appUrl: string | null;
  readonly canEdit: boolean;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const [label, setLabel] = useState("Our website");
  /** Held in state because the server cannot compose this address again. */
  const [made, setMade] = useState<{ url: string | null; secret: string } | null>(null);
  const [pasted, setPasted] = useState("");

  const live = links.filter((l) => l.revokedAt === null);

  const mint = async (): Promise<void> => {
    await submit(async () => {
      const created = await client.mintAgreementLink(label.trim());
      setMade({ url: created.url, secret: created.secret });
      try {
        if (created.url !== null && appUrl === null) await client.setFieldAgreementLink(created.url);
      } finally {
        reload();
      }
    });
  };

  const useInApp = async (url: string | null): Promise<void> => {
    await submit(async () => {
      await client.setFieldAgreementLink(url);
      setPasted("");
      reload();
    });
  };

  const revoke = async (id: string): Promise<void> => {
    await submit(async () => {
      await client.revokeAgreementLink(id);
      reload();
    });
  };

  return (
    <>
      {made === null ? null : (
        <div className="notice" role="status">
          <p>
            <strong>Here it is — this once.</strong> Only a fingerprint of it is
            kept, so it cannot be shown again. Save it somewhere now; if it is
            lost, revoke it below and make another.
          </p>
          <CopyField
            label="Your signing link"
            value={made.url ?? made.secret}
            copies="signing link"
            hint={
              made.url === null
                ? "Your signing address is not configured yet, so this is the code rather than the whole link. Tell whoever set the server up."
                : "Anyone holding this can sign your agreement. That is what it is for — but it also means it cannot tell who signed."
            }
          />
          {made.url === null ? null : made.url === appUrl ? (
            <p className="hint small">The field app is using this link.</p>
          ) : (
            <p className="row">
              <button type="button" disabled={busy} onClick={() => void useInApp(made.url)}>
                Use this link in the field app
              </button>
            </p>
          )}
          <p className="row">
            <button type="button" className="secondary" onClick={() => setMade(null)}>
              I have saved it
            </button>
          </p>
        </div>
      )}

      {live.length === 0 ? (
        <p className="hint small">
          No link published yet.
        </p>
      ) : (
        <ul className="plain-list" data-testid="agreement-links">
          {live.map((link) => (
            <li key={link.id} className="row-between">
              <span>
                <strong>{link.label}</strong>
                <span className="small muted">
                  {" "}
                  — made {longDate(link.createdAt)}.{" "}
                  {link.signedCount === 0
                    ? "Nobody has signed through it yet."
                    : link.signedCount === 1
                      ? "One person has signed through it."
                      : `${link.signedCount} people have signed through it.`}
                  {link.inFieldApp ? " In the field app." : ""}
                </span>
              </span>
              {canEdit ? (
                <button
                  type="button"
                  className="secondary"
                  disabled={busy}
                  onClick={() => void revoke(link.id)}
                >
                  Revoke
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/*
        NOT A <form>. This component is embedded in the Company screen, which
        is itself one form, and a nested form is invalid HTML -- the browser
        drops the inner one and the button below would submit the OUTER form,
        saving the company profile instead of minting a link. So the button
        acts on click, and Enter in the box is wired to the same thing because
        a text field somebody cannot press Enter in reads as broken.
      */}
      {canEdit ? (
        <div className="stacked">
          <div className="field">
            <label htmlFor="agreement-link-label">What it is for</label>
            <input
              id="agreement-link-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (!busy && label.trim() !== "") void mint();
              }}
            />
          </div>
          <button
            type="button"
            disabled={busy || label.trim() === ""}
            onClick={() => void mint()}
          >
            {busy ? "Making…" : "Make a signing link"}
          </button>
        </div>
      ) : null}

      <h3>The link in the field app</h3>
      <p className="hint small">
        Surveyors can show this address to a customer from the job screen, as a
        link or a QR code. A signature made through it starts its own job.
      </p>
      <p data-testid="field-agreement-link">
        {appUrl === null ? (
          <span className="muted">The field app has no link yet.</span>
        ) : (
          <>
            The field app is using <code>{appUrl}</code>
          </>
        )}
      </p>
      {canEdit ? (
        <div className="stacked">
          <div className="field">
            <label htmlFor="field-agreement-link">Paste a link for the field app</label>
            <input
              id="field-agreement-link"
              type="url"
              value={pasted}
              placeholder="https://"
              onChange={(e) => setPasted(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (!busy && pasted.trim() !== "") void useInApp(pasted.trim());
              }}
            />
          </div>
          <p className="row">
            <button
              type="button"
              disabled={busy || pasted.trim() === ""}
              onClick={() => void useInApp(pasted.trim())}
            >
              Use this link
            </button>
            {appUrl === null ? null : (
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => void useInApp(null)}
              >
                Clear it
              </button>
            )}
          </p>
        </div>
      ) : null}
      <Failure error={error} what="that signing link" />
    </>
  );
}
